import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppStore } from '../store';

interface BookingCalendarProps {
  homeId: string;
  selectedStart: string;
  selectedEnd: string;
  onSelectStart: (date: string) => void;
  onSelectEnd?: (date: string) => void;
  mode?: 'saturday' | 'flexible';
}

const DAY_HEADERS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

// Format a Date as a local-timezone YYYY-MM-DD string.
// NOTE: toISOString() returns UTC, which shifts local midnight to the previous
// day in any UTC+n timezone (e.g., CEST) and breaks date-cell comparisons.
const toDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Parse a YYYY-MM-DD string as local midnight (not UTC midnight).
const parseLocalDate = (str: string): Date => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  homeId,
  selectedStart,
  selectedEnd,
  onSelectStart,
  onSelectEnd,
  mode = 'flexible',
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const { getBookingsForHome } = useAppStore();
  const approvedBookings = getBookingsForHome(homeId).filter(b => b.status === 'approved');

  const isBooked = (date: Date): boolean => {
    const t = date.getTime();
    return approvedBookings.some(b => {
      const start = new Date(b.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(b.endDate);
      end.setHours(0, 0, 0, 0);
      return t >= start.getTime() && t < end.getTime();
    });
  };

  const buildGrid = (year: number, month: number): (Date | null)[] => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    // Start grid on Monday: getDay() 0=Sun→6, 1=Mon→0 ... 6=Sat→5
    const startOffset = (first.getDay() + 6) % 7;
    const cells: (Date | null)[] = Array(startOffset).fill(null);
    for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const rangeCrossesBooking = (start: Date, end: Date): boolean => {
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      if (isBooked(new Date(d))) return true;
    }
    return false;
  };

  const handleClick = (date: Date) => {
    if (date < today || isBooked(date)) return;

    if (mode === 'saturday') {
      if (date.getDay() !== 6) return;
      onSelectStart(toDateStr(date));
      return;
    }

    // Flexible mode: two-step selection
    const hasStart = Boolean(selectedStart);
    const hasEnd = Boolean(selectedEnd);

    if (!hasStart || (hasStart && hasEnd)) {
      // Start fresh
      onSelectStart(toDateStr(date));
      return;
    }

    // Have start, no end - try to set end
    const startD = parseLocalDate(selectedStart);
    if (date <= startD) {
      // Clicked at or before start -> use as new start
      onSelectStart(toDateStr(date));
      return;
    }
    if (rangeCrossesBooking(startD, date)) {
      // Range invalid - restart with this date
      onSelectStart(toDateStr(date));
      return;
    }
    onSelectEnd?.(toDateStr(date));
  };

  const selectedStartTime = selectedStart ? parseLocalDate(selectedStart).getTime() : null;
  const selectedEndTime = selectedEnd ? parseLocalDate(selectedEnd).getTime() : null;

  const grid = buildGrid(viewYear, viewMonth);

  return (
    <div className="space-y-2 select-none">
      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={prevMonth}
          className="p-1 rounded hover:bg-muted transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="p-1 rounded hover:bg-muted transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7">
        {DAY_HEADERS.map(d => (
          <div key={d} className="text-center text-[10px] font-medium text-muted-foreground py-0.5">
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {grid.map((date, i) => {
          if (!date) return <div key={i} className="h-7" />;

          const t = date.getTime();
          const isSat = date.getDay() === 6;
          const isPast = date < today;
          const booked = isBooked(date);
          const isStart = selectedStartTime !== null && t === selectedStartTime;
          const isEnd = selectedEndTime !== null && t === selectedEndTime;
          const inRange =
            selectedStartTime !== null &&
            selectedEndTime !== null &&
            t > selectedStartTime &&
            t < selectedEndTime;

          let cellCls =
            'h-7 flex items-center justify-center text-[11px] rounded transition-colors ';

          if (booked) {
            cellCls += 'line-through text-red-400 bg-red-50 cursor-not-allowed';
          } else if (isPast) {
            cellCls += 'text-muted-foreground/30 cursor-default';
          } else if (isStart || isEnd) {
            cellCls += 'bg-primary text-primary-foreground font-bold cursor-pointer';
          } else if (inRange) {
            cellCls += 'bg-primary/15 text-primary cursor-pointer';
          } else {
            cellCls += `text-foreground hover:bg-primary/10 cursor-pointer${isSat ? ' font-semibold' : ''}`;
          }

          return (
            <div key={i} className={cellCls} onClick={() => handleClick(date)}>
              {date.getDate()}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 pt-1 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-flex w-4 h-4 rounded hover:bg-primary/10 items-center justify-center text-foreground text-[10px]">T</span>
          Verfügbar
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-flex w-4 h-4 rounded bg-red-50 items-center justify-center text-red-400 text-[10px] line-through">T</span>
          Belegt
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-4 h-4 rounded bg-primary" />
          Ausgewählt
        </span>
      </div>
    </div>
  );
};

export default BookingCalendar;
